import { forwardRef, useCallback, useEffect, useState } from "react";
import { FaRegCopy } from "react-icons/fa";
import { HiCheck } from "react-icons/hi";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import ruby from "react-syntax-highlighter/dist/cjs/languages/prism/ruby";
import python from "react-syntax-highlighter/dist/cjs/languages/prism/python";
import typescript from "react-syntax-highlighter/dist/cjs/languages/prism/typescript";
import go from "react-syntax-highlighter/dist/cjs/languages/prism/go";
import bash from "react-syntax-highlighter/dist/cjs/languages/prism/bash";
import csharp from "react-syntax-highlighter/dist/cjs/languages/prism/csharp";
import docker from "react-syntax-highlighter/dist/cjs/languages/prism/docker";
import polarSyntaxHighlighting from "@/src/polarSyntaxHighlighting";
import { ThemedToken } from "shiki";
import { useIsDark } from "@/src/theme/useIsDark";

const THEME = {
  'code[class*="language-"], pre[class*="language-"]': {
    color: "rgba(0, 83, 179, 0.05)",
  },
  keyword: {
    color: "#ff0078",
    fontWeight: "bold",
  },
  comment: {
    color: "#999",
  },
  string: {
    color: "#028265",
  },
  url: {
    color: "#028265",
  },
  "attr-value": {
    color: "#028265",
  },
  variable: {
    color: "#0076ff",
  },
  "language-javascript": {
    color: "#0076ff",
  },
  "attr-name": {
    color: "#d9931e",
    fontStyle: "normal",
  },
  punctuation: {
    color: "#333",
  },
  operator: {
    color: "#333",
  },
  number: {
    color: "#0076ff",
  },
  function: {
    color: "#0076ff",
    fontWeight: "bold",
  },
  tag: {
    color: "#0076ff",
  },
  boolean: {
    color: "#d9931e",
  },
  regex: {
    color: "#d9931e",
  },
};

const THEME_DARK = {
  'code[class*="language-"], pre[class*="language-"]': {
    color: "#d4d4d4",
  },
  keyword: {
    color: "#ff6ba6",
    fontWeight: "bold",
  },
  comment: {
    color: "#6a737d",
  },
  string: {
    color: "#4ec9b0",
  },
  url: {
    color: "#4ec9b0",
  },
  "attr-value": {
    color: "#4ec9b0",
  },
  variable: {
    color: "#6cb6ff",
  },
  "language-javascript": {
    color: "#6cb6ff",
  },
  "attr-name": {
    color: "#e6b450",
    fontStyle: "normal",
  },
  punctuation: {
    color: "#cccccc",
  },
  operator: {
    color: "#cccccc",
  },
  number: {
    color: "#6cb6ff",
  },
  function: {
    color: "#6cb6ff",
    fontWeight: "bold",
  },
  tag: {
    color: "#6cb6ff",
  },
  boolean: {
    color: "#e6b450",
  },
  regex: {
    color: "#e6b450",
  },
};

SyntaxHighlighter.registerLanguage("ruby", ruby);
SyntaxHighlighter.registerLanguage("python", python);
SyntaxHighlighter.registerLanguage("typescript", typescript);
SyntaxHighlighter.registerLanguage("go", go);
SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("csharp", csharp);
SyntaxHighlighter.registerLanguage("docker", docker);

export interface CopyableCodeBlockProps {
  code: string;
  language:
    | "ruby"
    | "python"
    | "typescript"
    | "node.js"
    | "go"
    | "bash"
    | "csharp"
    | "docker";
  showLineNumbers?: boolean;
  standalone?: boolean;
  lines?: number[];
  onCopy?: () => void;
}

export const CopyableCodeBlock = forwardRef<
  HTMLButtonElement,
  CopyableCodeBlockProps
>((props, ref) => {
  const { code, language, showLineNumbers, standalone, lines, onCopy } = props;
  const [copied, setCopied] = useState(false);
  const dark = useIsDark();

  const copy = useCallback(() => {
    navigator.clipboard.writeText(code);
    if (onCopy) {
      onCopy();
    }
    setCopied(true);
  }, [setCopied, code, onCopy]);
  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [copied]);

  const preClasses = standalone
    ? "whitespace-normal"
    : "overflow-auto no-scroll-bar";

  return (
    <div className={`bg-muted flex ${standalone ? "p-2 rounded my-2" : "p-4"}`}>
      <pre className={`grow text-xs ${preClasses}`}>
        <SyntaxHighlighter
          customStyle={{
            margin: 0,
            padding: "0.5rem",
            overflow: "auto",
            backgroundColor: "rgba(243, 244, 246, 0.05)",
          }}
          codeTagProps={{
            className: "text-xs",
          }}
          language={language}
          style={dark ? THEME_DARK : THEME}
          wrapLongLines={true}
          wrapLines={true}
          showLineNumbers={showLineNumbers || false}
          lineProps={(lineNumber: number) => {
            let style: any = { flexWrap: "wrap" };
            if (lines != null && lines.includes(lineNumber - 1)) {
              style.backgroundColor = dark ? "#14351f" : "#dbffdb";
            }
            return { style };
          }}
        >
          {code}
        </SyntaxHighlighter>
      </pre>
      <div className="border-l border-border mx-4 shrink-0"></div>
      <button className="w-5 shrink-0" onClick={copy} ref={ref}>
        {copied ? <HiCheck /> : <FaRegCopy />}
        <span className="sr-only">Copy to clipboard</span>
      </button>
    </div>
  );
});

CopyableCodeBlock.displayName = "CopyableCodeBlock";

export const CopyableCodeBlockPolar = forwardRef<
  HTMLButtonElement,
  { code: string; standalone?: boolean; onCopy?: () => void }
>((props, ref) => {
  const { code, standalone, onCopy } = props;
  const [copied, setCopied] = useState(false);

  const [parsedPolicy, setParsedPolicy] = useState<ThemedToken[][] | null>(
    null
  );
  useEffect(() => {
    if (!code) {
      return;
    }
    polarSyntaxHighlighting(code).then((parsedPolicy) =>
      setParsedPolicy(parsedPolicy)
    );
  }, [code]);

  const copy = useCallback(() => {
    navigator.clipboard.writeText(code);
    if (onCopy) {
      onCopy();
    }
    setCopied(true);
  }, [setCopied, code, onCopy]);
  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timeout);
    }
  }, [copied]);

  const preClasses = standalone
    ? "whitespace-normal"
    : "overflow-auto no-scroll-bar";

  if (parsedPolicy == null) {
    return <div></div>;
  }

  return (
    <div className={`bg-muted flex ${standalone ? "p-2 rounded my-2" : "p-4"}`}>
      <pre className={`grow text-xs ${preClasses}`}>
        <code className="text-xs whitespace-pre-wrap">
          {parsedPolicy.map((line: ThemedToken[], index: number) => {
            return (
              <div key={index}>
                <span className="inline-block text-right select-none text-muted-foreground pr-[1em] min-w-[2.25em]">
                  {index + 1}
                </span>
                {line.map((token: ThemedToken, index: number) => (
                  <span style={{ color: token.color }} key={index}>
                    {token.content}
                  </span>
                ))}
              </div>
            );
          })}
        </code>
      </pre>
      <div className="border-l border-border mx-4 shrink-0"></div>
      <button className="w-5 shrink-0" onClick={copy} ref={ref}>
        {copied ? <HiCheck /> : <FaRegCopy />}
        <span className="sr-only">Copy to clipboard</span>
      </button>
    </div>
  );
});

CopyableCodeBlockPolar.displayName = "CopyableCodeBlockPolar";
